<template>
  <div class="product-form-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <el-button @click="$router.go(-1)" size="medium" class="back-btn">
            <i class="el-icon-arrow-left"></i> 返回
          </el-button>
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-goods"></i>
              {{ isEdit ? '编辑商品' : '新增商品' }}
            </h1>
            <p class="page-subtitle">{{ isEdit ? '修改商品信息，完善产品资料' : '添加新商品到系统，完善产品信息' }}</p>
          </div>
        </div>
        <div class="header-actions">
          <el-button @click="$router.go(-1)" size="medium">
            <i class="el-icon-close"></i> 取消
          </el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitting" size="medium">
            <i class="el-icon-check"></i> {{ isEdit ? '保存修改' : '创建商品' }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-container">
      <div class="form-section">
        <el-form
          ref="productForm"
          :model="formData"
          :rules="formRules"
          label-width="120px"
          v-loading="loading"
          class="product-form"
        >
          <!-- 基本信息卡片 -->
          <div class="form-card">
            <div class="card-header">
              <h3>
                <i class="el-icon-info"></i>
                基本信息
              </h3>
              <p>商品的基础信息和描述</p>
            </div>
            <div class="card-content">
              <div class="form-grid">
                <div class="form-item">
                  <label class="form-label">
                    <i class="el-icon-goods"></i>
                    商品名称 <span class="required">*</span>
                  </label>
                  <el-form-item prop="product_name">
                    <el-input 
                      v-model="formData.product_name" 
                      placeholder="请输入商品名称"
                      size="large"
                      maxlength="100"
                      show-word-limit
                    />
                  </el-form-item>
                </div>
                
                <div class="form-item">
                  <label class="form-label">
                    <i class="el-icon-postcard"></i>
                    商品编码 <span class="required">*</span>
                  </label>
                  <el-form-item prop="product_code">
                    <el-input 
                      v-model="formData.product_code" 
                      placeholder="请输入商品编码"
                      size="large"
                    />
                  </el-form-item>
                </div>
              </div>

              <div class="form-grid">
                <div class="form-item">
                  <label class="form-label">
                    <i class="el-icon-menu"></i>
                    商品分类
                  </label>
                  <el-form-item prop="category">
                    <el-select 
                      v-model="formData.category" 
                      placeholder="请选择商品分类"
                      filterable
                      allow-create
                      size="large"
                      style="width: 100%"
                    >
                      <el-option
                        v-for="category in categories"
                        :key="category.category"
                        :label="category.category"
                        :value="category.category"
                      />
                    </el-select>
                  </el-form-item>
                </div>
                
                <div class="form-item">
                  <label class="form-label">
                    <i class="el-icon-box"></i>
                    销售单位 <span class="required">*</span>
                  </label>
                  <el-form-item prop="unit">
                    <el-input 
                      v-model="formData.unit" 
                      placeholder="如：个、盒、瓶等"
                      size="large"
                    />
                  </el-form-item>
                </div>
              </div>

              <div class="form-item full-width">
                <label class="form-label">
                  <i class="el-icon-document"></i>
                  商品描述
                </label>
                <el-form-item>
                  <el-input
                    v-model="formData.description"
                    type="textarea"
                    :rows="4"
                    placeholder="请详细描述商品的特点、功效、使用方法等信息..."
                    maxlength="500"
                    show-word-limit
                  />
                </el-form-item>
              </div>
            </div>
          </div>

          <!-- 商品图片卡片 -->
          <div class="form-card">
            <div class="card-header">
              <h3>
                <i class="el-icon-picture"></i>
                商品图片
              </h3>
              <p>上传商品的主图和详情图片</p>
            </div>
            <div class="card-content">
              <div class="form-item full-width">
                <label class="form-label">
                  <i class="el-icon-camera"></i>
                  商品主图 <span class="required">*</span>
                </label>
                <el-form-item prop="main_image">
                  <div class="image-upload-section">
                    <div class="main-image-upload">
                      <el-upload
                        class="image-uploader"
                        action=""
                        :show-file-list="false"
                        :http-request="handleMainImageUpload"
                        :before-upload="beforeImageUpload"
                      >
                        <div v-if="formData.main_image" class="uploaded-image">
                          <img :src="formData.main_image">
                          <div class="image-overlay">
                            <i class="el-icon-edit"></i>
                          </div>
                        </div>
                        <div v-else class="upload-placeholder">
                          <i class="el-icon-plus"></i>
                          <p>点击上传主图</p>
                        </div>
                      </el-upload>
                    </div>
                    <div class="upload-tips">
                      <div class="tip-item">
                        <i class="el-icon-info"></i>
                        <span>建议尺寸：800x800 像素</span>
                      </div>
                      <div class="tip-item">
                        <i class="el-icon-warning-outline"></i>
                        <span>支持 JPG、PNG 格式，大小不超过 5MB</span>
                      </div>
                    </div>
                  </div>
                </el-form-item>
              </div>

              <div class="form-item full-width">
                <label class="form-label">
                  <i class="el-icon-picture-outline"></i>
                  商品详情图
                </label>
                <el-form-item>
                  <div class="detail-images-section">
                    <div class="detail-images-grid">
                      <div
                        v-for="(image, index) in formData.detail_images"
                        :key="index"
                        class="detail-image-item"
                      >
                        <img :src="image">
                        <div class="image-overlay">
                          <el-button
                            type="danger"
                            icon="el-icon-delete"
                            size="mini"
                            circle
                            @click="removeDetailImage(index)"
                          />
                        </div>
                      </div>
                      <el-upload
                        v-if="formData.detail_images.length < 9"
                        class="detail-image-upload"
                        action=""
                        :show-file-list="false"
                        :http-request="handleDetailImageUpload"
                        :before-upload="beforeImageUpload"
                      >
                        <div class="upload-placeholder small">
                          <i class="el-icon-plus"></i>
                          <p>添加图片</p>
                        </div>
                      </el-upload>
                    </div>
                    <div class="upload-tips">
                      <div class="tip-item">
                        <i class="el-icon-info"></i>
                        <span>最多可上传9张详情图，用于展示商品的不同角度和细节</span>
                      </div>
                    </div>
                  </div>
                </el-form-item>
              </div>
            </div>
          </div>

          <!-- 价格库存卡片 -->
          <div class="form-card">
            <div class="card-header">
              <h3>
                <i class="el-icon-money"></i>
                价格库存
              </h3>
              <p>设置商品的价格和库存信息</p>
            </div>
            <div class="card-content">
              <div class="form-grid three-columns">
                <div class="form-item">
                  <label class="form-label">
                    <i class="el-icon-sell"></i>
                    基础价格 <span class="required">*</span>
                  </label>
                  <el-form-item prop="base_price">
                    <el-input-number
                      v-model="formData.base_price"
                      :precision="2"
                      :min="0"
                      :max="9999999.99"
                      size="large"
                      style="width: 100%"
                      @change="handlePriceChange"
                    >
                      <template slot="prepend">¥</template>
                    </el-input-number>
                  </el-form-item>
                </div>
                
                <div class="form-item">
                  <label class="form-label">
                    <i class="el-icon-money"></i>
                    原价（划线价）
                  </label>
                  <el-form-item prop="original_price">
                    <el-input-number
                      v-model="formData.original_price"
                      :min="0"
                      :precision="2"
                      size="large"
                      style="width: 100%"
                      placeholder="用于显示对比价格"
                    >
                      <template slot="prepend">¥</template>
                    </el-input-number>
                  </el-form-item>
                </div>
                
                <div class="form-item">
                  <label class="form-label">
                    <i class="el-icon-sort"></i>
                    排序序号
                  </label>
                  <el-form-item prop="sort_order">
                    <el-input-number
                      v-model="formData.sort_order"
                      :min="0"
                      size="large"
                      style="width: 100%"
                      placeholder="数值越大排序越靠前"
                    />
                  </el-form-item>
                </div>
                
                <!-- 商品状态设置 -->
                <div class="form-row">
                  <div class="form-item full-width">
                    <label class="form-label">
                      <i class="el-icon-star-on"></i>
                      商品状态
                    </label>
                    <div class="status-switches">
                      <el-form-item prop="is_featured">
                        <el-switch
                          v-model="formData.is_featured"
                          active-text="推荐商品"
                          inactive-text="普通商品"
                          active-color="#409eff"
                          size="medium"
                        />
                      </el-form-item>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 折上折设置卡片 -->
          <div class="form-card">
            <div class="card-header">
              <h3>
                <i class="el-icon-discount"></i>
                折上折设置
              </h3>
              <p>为会员用户提供额外折扣优惠，提升购买转化率</p>
            </div>
            <div class="card-content">
              <div class="extra-discount-section">
                <div class="discount-switch-section">
                  <el-form-item prop="enable_extra_discount">
                    <el-switch
                      v-model="formData.enable_extra_discount"
                      active-text="开启折上折"
                      inactive-text="关闭折上折"
                      active-color="#67c23a"
                      size="large"
                      @change="handleExtraDiscountChange"
                    />
                  </el-form-item>
                  <div class="switch-description">
                    <i class="el-icon-info"></i>
                    <span>开启后，会员可在原有折扣基础上享受额外折扣</span>
                  </div>
                </div>
                
                <div v-if="formData.enable_extra_discount" class="discount-settings">
                  <div class="discount-rate-setting">
                    <label class="rate-label">
                      <i class="el-icon-s-data"></i>
                      折扣率设置
                    </label>
                    <div class="rate-input-group">
                      <el-form-item prop="extra_discount_rate" class="rate-input">
                        <el-input-number
                          v-model="formData.extra_discount_rate"
                          :min="0.01"
                          :max="0.99"
                          :step="0.01"
                          :precision="2"
                          size="large"
                          style="width: 150px"
                          @change="handleDiscountRateChange"
                        />
                      </el-form-item>
                      <span class="rate-description">
                        = {{ (formData.extra_discount_rate * 10).toFixed(1) }}折
                      </span>
                    </div>
                    <div class="rate-note">
                      <i class="el-icon-warning-outline"></i>
                      <span>折扣率范围：0.01-0.99，数值越小折扣力度越大</span>
                    </div>
                  </div>
                  
                  <div class="member-level-setting">
                    <label class="level-label">
                      <i class="el-icon-user"></i>
                      适用会员等级
                    </label>
                    <div class="level-select-group">
                      <el-form-item prop="applicable_member_levels" class="level-select">
                        <el-select
                          v-model="formData.applicable_member_levels"
                          multiple
                          placeholder="请选择适用的会员等级"
                          size="large"
                          style="width: 100%"
                          @change="handleMemberLevelChange"
                        >
                          <el-option
                            v-for="level in memberLevels"
                            :key="level.id"
                            :label="level.name"
                            :value="level.id"
                            class="member-level-option"
                          >
                            <div class="level-option-name">
                              <span class="level-name-text">{{ level.name }}</span>
                              <span class="level-code-text" v-if="level.level_code && level.level_code !== 'NORMAL'">
                                ({{ level.level_code }})
                              </span>
                            </div>
                            <span class="level-option-discount">{{ (level.discount_rate * 10).toFixed(1) }}折</span>
                          </el-option>
                        </el-select>
                      </el-form-item>
                      <div class="level-description">
                        <i class="el-icon-info"></i>
                        <span v-if="formData.applicable_member_levels && formData.applicable_member_levels.length > 0">
                          已选择 {{ formData.applicable_member_levels.length }} 个会员等级
                        </span>
                        <span v-else>
                          未选择时默认适用于所有会员等级
                        </span>
                      </div>
                    </div>
                    <div class="level-preview" v-if="selectedMemberLevels.length > 0">
                      <div class="level-preview-header">
                        <i class="el-icon-s-check"></i>
                        <span>选中的会员等级</span>
                      </div>
                      <div class="level-preview-list">
                        <div 
                          v-for="level in selectedMemberLevels" 
                          :key="level.id"
                          class="level-preview-item"
                        >
                          <div class="level-info">
                            <div class="level-name-section">
                              <span class="level-name">{{ level.name }}</span>
                              <span class="level-code">({{ level.level_code }})</span>
                            </div>
                            <span class="level-discount">{{ (level.discount_rate * 10).toFixed(1) }}折</span>
                          </div>
                          <div class="level-requirements" v-if="level.min_purchase_amount > 0">
                            <i class="el-icon-shopping-bag-1"></i>
                            <span>最低消费：¥{{ level.min_purchase_amount.toFixed(0) }}</span>
                          </div>
                          <div class="level-final-price" v-if="formData.base_price">
                            最终价格：¥{{ calculateFinalPrice(level.discount_rate).toFixed(2) }}
                            <span class="price-diff" v-if="level.discount_rate < 1">
                              (立省¥{{ (formData.base_price * formData.extra_discount_rate * (1 - level.discount_rate)).toFixed(2) }})
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div v-if="false" class="discount-preview">
                    <div class="preview-header">
                      <i class="el-icon-view"></i>
                      <span>价格预览</span>
                    </div>
                    <div class="preview-content">
                      <div class="preview-item">
                        <span class="preview-label">商品原价：</span>
                        <span class="preview-value original">¥{{ formData.base_price.toFixed(2) }}</span>
                      </div>
                      <div class="preview-item">
                        <span class="preview-label">折上折价：</span>
                        <span class="preview-value discount">¥{{ (formData.base_price * formData.extra_discount_rate).toFixed(2) }}</span>
                        <span class="preview-savings">（立省¥{{ (formData.base_price * (1 - formData.extra_discount_rate)).toFixed(2) }}）</span>
                      </div>
                    </div>
                    <div class="preview-note">
                      <i class="el-icon-info"></i>
                      <span>注：会员购买时还会在此基础上再享受对应等级的会员折扣</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 富文本描述卡片 -->
          <div class="form-card">
            <div class="card-header">
              <h3>
                <i class="el-icon-document"></i>
                详细描述
              </h3>
              <p>商品的详细介绍，支持富文本格式</p>
            </div>
            <div class="card-content">
              <div class="form-item full-width">
                <label class="form-label">
                  <i class="el-icon-edit"></i>
                  富文本描述
                </label>
                <el-form-item prop="rich_description">
                  <rich-text-editor
                    v-model="formData.rich_description"
                    ref="richDescriptionEditor"
                    placeholder="请输入商品详细描述，支持插入图片..."
                    height="400px"
                    :image-upload-options="{
                      type: 'product',
                      module: 'product'
                    }"
                    @image-upload-success="onImageUploadSuccess"
                    @image-upload-error="onImageUploadError"
                  />
                </el-form-item>
                <div class="rich-description-tips">
                  <el-alert
                    title="使用富文本编辑器编辑内容，支持文字格式、标题、列表、链接、图片上传等功能"
                    type="info"
                    show-icon
                    :closable="false"
                  />
                </div>
              </div>
            </div>
          </div>



        </el-form>
      </div>

      <!-- 右侧预览区域 -->
      <div class="preview-section">
        <div class="preview-card sticky">
          <div class="preview-header">
            <h4>
              <i class="el-icon-view"></i>
              商品预览
            </h4>
          </div>
          <div class="preview-content">
            <div class="product-preview">
              <div class="preview-image">
                <img v-if="formData.main_image" :src="formData.main_image" alt="商品预览">
                <div v-else class="no-image">
                  <i class="el-icon-picture-outline"></i>
                  <p>暂无图片</p>
                </div>
              </div>
              <div class="preview-info">
                <h5>{{ formData.product_name || '商品名称' }}</h5>
                <p class="preview-code">编码：{{ formData.product_code || '待设置' }}</p>
                <div class="preview-price">
                  <span class="price">¥{{ formData.base_price || '0.00' }}</span>
                  <span class="unit">/ {{ formData.unit || '件' }}</span>
                </div>
                <div class="preview-original-price" v-if="formData.original_price">
                  <span class="original-price-label">原价：</span>
                  <span class="original-price-value">¥{{ formData.original_price }}</span>
                </div>
                <div class="preview-status">
                  <el-tag v-if="formData.is_featured" type="warning" size="mini">
                    <i class="el-icon-star-on"></i> 推荐商品
                  </el-tag>
                </div>
                <div class="preview-category" v-if="formData.category">
                  <el-tag size="small">{{ formData.category }}</el-tag>
                </div>
                
                <!-- 富文本描述预览 -->
                <div class="preview-description" v-if="formData.rich_description">
                  <h6 class="description-title">商品描述：</h6>
                  <div 
                    class="description-content"
                    v-html="formData.rich_description"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createProduct, updateProduct, getProduct, getProductCategories } from '@/api/product'
import { uploadImage, validateFileType, validateFileSize, FILE_TYPES, FILE_SIZE_LIMITS } from '@/api/upload'
import { getMembershipLevelList } from '@/api/membershipLevel'
import { processImageUrl } from '@/utils/config'
import RichTextEditor from '@/components/common/RichTextEditor.vue'
import store from '@/store'

export default {
  name: 'ProductForm',
  
  components: {
    RichTextEditor
  },
  data() {
    return {
      loading: false,
      submitting: false,
      isEdit: false,
      categories: [],
      
      formData: {
        product_name: '',
        product_code: '',
        category: '',
        description: '',
        rich_description: '',
        main_image: '',
        detail_images: [],
        unit: '',
        base_price: 0,
        original_price: 0,
        is_featured: false,
        is_active: true,
        sort_order: 0,
        enable_extra_discount: false,
        extra_discount_rate: 0.85,
        applicable_member_levels: []
      },
      
      memberLevels: [], // 会员等级列表
      
      formRules: {
        product_name: [
          { required: true, message: '请输入商品名称', trigger: 'blur' },
          { min: 2, max: 100, message: '商品名称长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        product_code: [
          { required: true, message: '请输入商品编码', trigger: 'blur' },
          { min: 1, max: 50, message: '商品编码长度在 1 到 50 个字符', trigger: 'blur' }
        ],
        unit: [
          { required: true, message: '请输入销售单位', trigger: 'blur' }
        ],
        base_price: [
          { required: true, message: '请输入基础价格', trigger: ['blur', 'change'] },
          { 
            validator: (rule, value, callback) => {
              if (value === '' || value === null || value === undefined) {
                callback(new Error('请输入基础价格'))
              } else {
                const price = Number(value)
                if (isNaN(price) || price <= 0) {
                  callback(new Error('价格必须大于0'))
                } else {
                  callback()
                }
              }
            }, 
            trigger: ['blur', 'change'] 
          }
        ],
        rich_description: [
          { 
            validator: (rule, value, callback) => {
              if (!value) {
                // 富文本描述是可选的
                callback()
                return
              }
              // 去除HTML标签获取纯文本内容
              const textContent = value.replace(/<[^>]*>/g, '').trim()
              if (textContent.length > 0 && textContent.length < 5) {
                callback(new Error('商品描述内容不能少于 5 个字符'))
              } else {
                callback()
              }
            }, 
            trigger: 'blur' 
          }
        ],
        extra_discount_rate: [
          { 
            validator: (rule, value, callback) => {
              if (!this.formData.enable_extra_discount) {
                callback()
                return
              }
              if (value === '' || value === null || value === undefined) {
                callback(new Error('开启折上折时，折扣率不能为空'))
              } else {
                const rate = Number(value)
                if (isNaN(rate) || rate <= 0 || rate >= 1) {
                  callback(new Error('折扣率必须在0.01-0.99之间'))
                } else {
                  callback()
                }
              }
            }, 
            trigger: ['blur', 'change'] 
          }
        ]
      }
    }
  },
  
  created() {
    this.isEdit = !!this.$route.params.id
    this.loadCategories()
    this.loadMemberLevels() // 加载会员等级数据
    
    if (this.isEdit) {
      this.loadProductData()
    } else {
      // 新增模式下初始化表单
      this.initializeForm()
    }
  },
  
  computed: {
    // 获取选中的会员等级详情
    selectedMemberLevels() {
      if (!this.formData.applicable_member_levels || this.formData.applicable_member_levels.length === 0) {
        return []
      }
      return this.memberLevels.filter(level => 
        this.formData.applicable_member_levels.includes(level.id)
      )
    }
  },
  
  methods: {
    // 已移除，使用新的上传API
    
    async loadCategories() {
      try {
        const response = await getProductCategories()
        this.categories = response.data.categories
      } catch (error) {
        console.error('获取分类列表失败:', error)
      }
    },
    
    async loadMemberLevels() {
      try {
        const response = await getMembershipLevelList()
        if (response.code === 200) {
          // 根据实际API响应格式转换数据
          this.memberLevels = (response.data.list || [])
            .filter(level => level.isActive) // 只显示激活的会员等级
            .map(level => ({
              id: level.id,
              name: level.levelName,
              discount_rate: level.discountRate,
              level_code: level.levelCode,
              min_purchase_amount: level.minPurchaseAmount
            }))
        } else {
          throw new Error(response.message || '获取会员等级失败')
        }
      } catch (error) {
        console.error('获取会员等级失败:', error)
        // 如果API调用失败，使用与实际数据格式相近的模拟数据
        this.memberLevels = [
          { id: 1, name: '普通用户', discount_rate: 1.0, level_code: 'NORMAL' },
          { id: 3, name: '白银会员', discount_rate: 0.88, level_code: 'SILVER' },
          { id: 4, name: '黄金会员', discount_rate: 0.74, level_code: 'GOLD' },
          { id: 5, name: '钻石会员', discount_rate: 0.60, level_code: 'PLATINUM' }
        ]
      }
    },
    
    async loadProductData() {
      const productId = this.$route.params.id
      this.loading = true
      
      try {
        const response = await getProduct(productId)
        const product = response.data
        
        this.formData = {
          product_name: product.product_name,
          product_code: product.product_code,
          category: product.category,
          description: product.description,
          rich_description: product.rich_description || '',
          main_image: processImageUrl(product.main_image),
          detail_images: (product.detail_images || []).map(url => processImageUrl(url)),
          unit: product.unit,
          base_price: product.base_price,
          original_price: product.original_price || 0,
          is_featured: product.is_featured || false,
          is_active: product.is_active !== false,
          sort_order: product.sort_order || 0,
          enable_extra_discount: product.enable_extra_discount || false,
          extra_discount_rate: product.extra_discount_rate || 0.85,
          applicable_member_levels: product.applicable_member_levels || []
        }
        
      } catch (error) {
        this.$message.error('获取商品信息失败')
        this.$router.go(-1)
      } finally {
        this.loading = false
      }
    },
    
    removeDetailImage(index) {
      this.formData.detail_images.splice(index, 1)
    },
    
    beforeImageUpload(file) {
      const isValidType = validateFileType(file, FILE_TYPES.IMAGES)
      const isValidSize = validateFileSize(file, FILE_SIZE_LIMITS.IMAGE)
      
      if (!isValidType) {
        this.$message.error('上传的文件必须是图片格式!')
      }
      if (!isValidSize) {
        this.$message.error('上传的图片大小不能超过 5MB!')
      }
      return isValidType && isValidSize
    },
    
    async handleMainImageUpload(options) {
      const file = options.file
      
      try {
        const response = await uploadImage(file, {
          type: 'product',
          module: 'product'
        })
        
        if (response.code === 200) {
          this.formData.main_image = processImageUrl(response.data.url)
          this.$message.success('主图上传成功')
        } else {
          this.$message.error(response.message || '主图上传失败')
        }
      } catch (error) {
        console.error('主图上传失败:', error)
        this.$message.error('主图上传失败')
      }
    },
    
    async handleDetailImageUpload(options) {
      const file = options.file
      
      if (this.formData.detail_images.length >= 9) {
        this.$message.error('最多只能上传9张详情图')
        return
      }
      
      try {
        const response = await uploadImage(file, {
          type: 'product',
          module: 'product'
        })
        
        if (response.code === 200) {
          this.formData.detail_images.push(processImageUrl(response.data.url))
          this.$message.success('详情图上传成功')
        } else {
          this.$message.error(response.message || '详情图上传失败')
        }
      } catch (error) {
        console.error('详情图上传失败:', error)
        this.$message.error('详情图上传失败')
      }
    },
    
    // 处理折上折开关变化
    handleExtraDiscountChange(enabled) {
      if (enabled) {
        // 开启折上折时，设置默认折扣率
        if (!this.formData.extra_discount_rate) {
          this.formData.extra_discount_rate = 0.85
        }
      } else {
        // 关闭折上折时，清除验证状态
        this.$nextTick(() => {
          if (this.$refs.productForm) {
            this.$refs.productForm.clearValidate('extra_discount_rate')
          }
        })
      }
    },

    // 处理折扣率变化
    handleDiscountRateChange(rate) {
      // 触发表单验证
      this.$nextTick(() => {
        if (this.$refs.productForm) {
          this.$refs.productForm.validateField('extra_discount_rate')
        }
      })
    },

    // 处理会员等级选择变化
    handleMemberLevelChange(selectedLevels) {
      // 可以在这里添加额外的逻辑处理
    },

    // 计算最终价格（折上折 + 会员折扣）
    calculateFinalPrice(memberDiscountRate) {
      if (!this.formData.base_price) return 0
      
      // 先应用折上折
      const afterExtraDiscount = this.formData.base_price * this.formData.extra_discount_rate
      
      // 再应用会员折扣
      const finalPrice = afterExtraDiscount * memberDiscountRate
      
      return finalPrice
    },

    // 处理价格变化，更新折上折预览
    handlePriceChange(price) {
      // 价格变化时不需要特殊处理，Vue的响应式会自动更新预览
    },

    handleSubmit() {
      // 检查表单引用是否存在
      if (!this.$refs.productForm) {
        console.error('表单引用不存在')
        this.$message.error('表单初始化失败，请刷新页面重试')
        return
      }
      
      this.$refs.productForm.validate(async (valid, invalidFields) => {
        if (valid) {
          await this.submitForm()
        } else {
          
          // 显示具体的验证错误信息
          const firstErrorField = Object.keys(invalidFields)[0]
          const firstError = invalidFields[firstErrorField][0]
          this.$message.error(firstError.message || '请检查表单数据是否填写完整')
          
          // 滚动到第一个错误字段
          this.$nextTick(() => {
            const errorElement = document.querySelector('.el-form-item.is-error')
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          })
        }
      })
    },
    
    async submitForm() {
      const submitData = {
        ...this.formData
      }
      
      this.submitting = true
      
      try {
        if (this.isEdit) {
          await updateProduct(this.$route.params.id, submitData)
          this.$message.success('商品更新成功')
        } else {
          const response = await createProduct(submitData)
          this.$message.success('商品创建成功')
        }
        
        // 返回到商品列表
        this.$router.push('/admin/products')
        
      } catch (error) {
        console.error('提交失败:', error)
        this.$message.error(this.isEdit ? '商品更新失败' : '商品创建失败')
      } finally {
        this.submitting = false
      }
    },
    
    // 富文本编辑器图片上传成功回调
    onImageUploadSuccess({ file, url, response }) {
      // 图片上传成功处理
    },
    
    // 富文本编辑器图片上传失败回调
    onImageUploadError(error) {
      console.error('富文本编辑器图片上传失败:', error)
    },
    
    // 初始化新建表单
    initializeForm() {
      // 确保数字字段有正确的类型
      this.formData.base_price = 0
      this.formData.original_price = 0
      this.formData.sort_order = 0
      
      // 确保表单在下次tick中完全渲染
      this.$nextTick(() => {
        if (this.$refs.productForm) {
          this.$refs.productForm.clearValidate()
        }
      })
    },
    
    // 重置表单
    resetForm() {
      this.formData = {
        product_name: '',
        product_code: '',
        category: '',
        description: '',
        rich_description: '',
        main_image: '',
        detail_images: [],
        unit: '',
        base_price: 0,
        original_price: 0,
        is_featured: false,
        is_active: true,
        sort_order: 0,
        enable_extra_discount: false,
        extra_discount_rate: 0.85
      }
      
      if (this.$refs.productForm) {
        this.$refs.productForm.resetFields()
        this.$refs.productForm.clearValidate()
      }
    },
    
     // 处理价格变化
     handlePriceChange(value) {
       // 确保价格是数字类型
       if (value !== null && value !== undefined) {
         this.formData.base_price = Number(value)
       }
     },
    
    
  }
}
</script>

<style scoped>
.product-form-container {
  min-height: 100vh;
  background: #f5f7fa;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 页面头部 */
.page-header {
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
  z-index: 100;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
}

.back-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.header-info {
  flex: 1;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title i {
  font-size: 32px;
}

.page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.header-actions .el-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
}

.header-actions .el-button--primary {
  background: rgba(255, 255, 255, 0.95);
  color: #667eea;
  border-color: transparent;
}

.header-actions .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.header-actions .el-button--primary:hover {
  background: white;
  color: #667eea;
}

/* 主容器 */
.main-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 30px;
  margin-bottom: 40px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

/* 表单卡片样式 */
.form-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  overflow: hidden;
  transition: all 0.3s ease;
}

.form-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.card-header {
  padding: 25px 30px 15px;
  border-bottom: 1px solid #f0f2f5;
  background: linear-gradient(135deg, #fafbfc, #f8f9fa);
}

.card-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-header h3 i {
  color: #667eea;
  font-size: 20px;
}

.card-header p {
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.card-content {
  padding: 30px;
}

/* 表单网格布局 */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 25px;
  margin-bottom: 20px;
}

.form-grid.three-columns {
  grid-template-columns: 1fr 1fr 1fr;
}

.form-item {
  display: flex;
  flex-direction: column;
}

.form-item.full-width {
  grid-column: 1 / -1;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-label i {
  color: #909399;
  font-size: 16px;
}

.form-label .required {
  color: #f56c6c;
  font-weight: 700;
}

/* 折上折设置样式 - 独立模块 */
.extra-discount-section {
  width: 100%;
}

.discount-switch-section {
  background: linear-gradient(135deg, #f6f9ff 0%, #e8f4f8 100%);
  border: 1px solid #d4ebf0;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  text-align: center;
}

.switch-description {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  color: #606266;
  font-size: 13px;
}

.switch-description i {
  color: #409eff;
}

.discount-settings {
  background: #ffffff;
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.discount-rate-setting {
  margin-bottom: 24px;
}

.rate-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
}

.rate-label i {
  color: #67c23a;
  font-size: 16px;
}

.rate-input-group {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.rate-input {
  margin: 0 !important;
}

.rate-description {
  font-size: 14px;
  color: #67c23a;
  font-weight: 600;
  padding: 4px 12px;
  background: #f0f9ff;
  border-radius: 20px;
  border: 1px solid #67c23a20;
}

.rate-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fdf6ec;
  border: 1px solid #faecd8;
  border-radius: 8px;
  color: #e6a23c;
  font-size: 13px;
}

.rate-note i {
  color: #e6a23c;
}

/* 会员等级设置样式 */
.member-level-setting {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
}

/* 会员等级下拉框选项样式 */
.member-level-option {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  padding: 8px 16px !important;
  line-height: 1.4 !important;
}

.level-option-name {
  font-weight: 500;
  color: #303133;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
}

.level-name-text {
  font-weight: 500;
}

.level-code-text {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}

.level-option-discount {
  color: #67c23a;
  font-weight: 600;
  font-size: 13px;
  margin-left: 12px;
}

/* 全局覆盖Element UI下拉框选项样式 */
/deep/ .el-select-dropdown__item.member-level-option {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  height: auto !important;
  line-height: 1.4 !important;
  padding: 10px 20px !important;
}

/deep/ .el-select-dropdown__item.member-level-option:hover {
  background-color: #f5f7fa;
}

.level-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
}

.level-label i {
  color: #409eff;
  font-size: 16px;
}

.level-select-group {
  margin-bottom: 16px;
}

.level-select {
  margin: 0 !important;
}

.level-description {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  color: #666;
  font-size: 13px;
}

.level-description i {
  color: #909399;
}

.level-preview {
  background: linear-gradient(135deg, #fff7e6 0%, #fff2cc 100%);
  border: 1px solid #ffd666;
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
}

.level-preview-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #fa8c16;
  margin-bottom: 12px;
}

.level-preview-header i {
  font-size: 16px;
}

.level-preview-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.level-preview-item {
  background: white;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.level-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.level-name-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.level-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.level-code {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}

.level-discount {
  font-size: 13px;
  color: #fa8c16;
  font-weight: 600;
  background: #fff7e6;
  padding: 2px 8px;
  border-radius: 12px;
}

.level-requirements {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  padding: 4px 8px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
  color: #606266;
}

.level-requirements i {
  color: #909399;
}

.level-final-price {
  font-size: 16px;
  color: #52c41a;
  font-weight: 700;
  text-align: right;
}

.price-diff {
  font-size: 12px;
  color: #f56c6c;
  margin-left: 8px;
}

.discount-preview {
  background: linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%);
  border: 1px solid #91d5ff;
  border-radius: 12px;
  padding: 20px;
  margin-top: 20px;
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #1890ff;
  margin-bottom: 16px;
}

.preview-header i {
  font-size: 16px;
}

.preview-content {
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
}

.preview-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.preview-item:last-child {
  margin-bottom: 0;
}

.preview-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.preview-value.original {
  font-size: 16px;
  color: #909399;
  text-decoration: line-through;
  font-weight: 500;
}

.preview-value.discount {
  font-size: 20px;
  color: #52c41a;
  font-weight: 700;
}

.preview-savings {
  font-size: 13px;
  color: #ff4d4f;
  font-weight: 600;
  margin-left: 8px;
}

.preview-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(24, 144, 255, 0.08);
  border-radius: 8px;
  color: #1890ff;
  font-size: 13px;
  line-height: 1.5;
}

.preview-note i {
  color: #1890ff;
  margin-top: 2px;
  flex-shrink: 0;
}

/* 图片上传样式 */
.image-upload-section {
  display: flex;
  gap: 30px;
  align-items: flex-start;
}

.main-image-upload {
  flex-shrink: 0;
}

.image-uploader :deep(.el-upload) {
  position: relative;
  border: 2px dashed #d9d9d9;
  border-radius: 12px;
  cursor: pointer;
  overflow: hidden;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.image-uploader :deep(.el-upload:hover) {
  border-color: #667eea;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
}

.uploaded-image {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
}

.uploaded-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s ease;
}

.uploaded-image:hover .image-overlay,
.detail-image-item:hover .image-overlay {
  opacity: 1;
}

.image-overlay i {
  color: white;
  font-size: 24px;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #8c939d;
}

.upload-placeholder i {
  font-size: 40px;
  margin-bottom: 10px;
}

.upload-placeholder p {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
}

.upload-placeholder.small {
  gap: 5px;
}

.upload-placeholder.small i {
  font-size: 24px;
  margin-bottom: 5px;
}

.upload-placeholder.small p {
  font-size: 12px;
}

.upload-tips {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tip-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #666;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #409eff;
}

.tip-item i {
  color: #409eff;
  font-size: 14px;
}

/* 详情图片样式 */
.detail-images-section {
  width: 100%;
}

.detail-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.detail-image-item {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
}

.detail-image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detail-image-upload :deep(.el-upload) {
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  cursor: pointer;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.detail-image-upload :deep(.el-upload:hover) {
  border-color: #667eea;
  background: #f0f4ff;
}


/* 开关样式 */
.switch-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.switch-text {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

/* 预览区域样式 */
.preview-section {
  position: sticky;
  top: 30px;
  height: fit-content;
}

.preview-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  overflow: hidden;
}

.preview-header {
  padding: 20px 25px 15px;
  border-bottom: 1px solid #f0f2f5;
  background: linear-gradient(135deg, #f0f4ff, #e8f2ff);
}

.preview-header h4 {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.preview-header i {
  color: #667eea;
  font-size: 18px;
}

.preview-content {
  padding: 25px;
}

.product-preview {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.preview-image {
  width: 100%;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #f0f2f5;
}

.preview-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #c0c4cc;
  background: #fafbfc;
}

.no-image i {
  font-size: 48px;
  margin-bottom: 10px;
}

.no-image p {
  margin: 0;
  font-size: 14px;
}

.preview-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.preview-info h5 {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0;
  line-height: 1.4;
}

.preview-code {
  font-size: 13px;
  color: #909399;
  margin: 0;
}

.preview-price {
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.price {
  font-size: 20px;
  font-weight: 700;
  color: #f56c6c;
}

.unit {
  font-size: 14px;
  color: #909399;
}

.preview-original-price {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  margin: 8px 0;
}

.original-price-label {
  color: #909399;
}

.original-price-value {
  color: #909399;
  text-decoration: line-through;
}

.preview-status {
  margin: 8px 0;
}

.status-switches {
  margin-top: 10px;
}

.rich-description-tips {
  margin-top: 10px;
}

.full-width {
  width: 100%;
}

.preview-category {
  margin-top: 8px;
}

/* 预览区域富文本描述样式 */
.preview-description {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.description-title {
  font-size: 14px;
  color: #303133;
  margin: 0 0 8px 0;
  font-weight: 600;
}

.description-content {
  font-size: 13px;
  line-height: 1.6;
  color: #606266;
  max-height: 200px;
  overflow-y: auto;
  word-break: break-word;
}

.description-content h1,
.description-content h2,
.description-content h3,
.description-content h4,
.description-content h5,
.description-content h6 {
  margin: 8px 0 4px 0;
  line-height: 1.4;
}

.description-content p {
  margin: 4px 0;
}

/* 预览区域列表样式 */
.description-content ul,
.description-content ol {
  margin: 8px 0;
  padding-left: 20px;
}

.description-content ul li {
  list-style-type: disc;
  margin: 2px 0;
}

.description-content ol li {
  list-style-type: decimal;
  margin: 2px 0;
}

/* 预览区域对齐样式 */
.description-content .ql-align-center {
  text-align: center;
}

.description-content .ql-align-right {
  text-align: right;
}

.description-content .ql-align-justify {
  text-align: justify;
}

/* 预览区域引用样式 */
.description-content blockquote {
  border-left: 4px solid #ccc;
  margin: 8px 0;
  padding: 4px 12px;
  background-color: #f9f9f9;
  font-style: italic;
}

/* 预览区域字体大小样式 - 支持内联样式和类格式 */
.description-content span[style*="font-size: 10px"] {
  font-size: 10px !important;
}

.description-content span[style*="font-size: 12px"] {
  font-size: 12px !important;
}

.description-content span[style*="font-size: 14px"] {
  font-size: 14px !important;
}

.description-content span[style*="font-size: 16px"] {
  font-size: 16px !important;
}

.description-content span[style*="font-size: 18px"] {
  font-size: 18px !important;
}

.description-content span[style*="font-size: 20px"] {
  font-size: 20px !important;
}

.description-content span[style*="font-size: 24px"] {
  font-size: 24px !important;
}

.description-content span[style*="font-size: 32px"] {
  font-size: 32px !important;
}

.description-content .ql-size-10px {
  font-size: 10px !important;
}

.description-content .ql-size-12px {
  font-size: 12px !important;
}

.description-content .ql-size-14px {
  font-size: 14px !important;
}

.description-content .ql-size-16px {
  font-size: 16px !important;
}

.description-content .ql-size-18px {
  font-size: 18px !important;
}

.description-content .ql-size-20px {
  font-size: 20px !important;
}

.description-content .ql-size-24px {
  font-size: 24px !important;
}

.description-content .ql-size-32px {
  font-size: 32px !important;
}

.description-content img {
  max-width: 100%;
  height: auto;
  margin: 4px 0;
  border-radius: 4px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .main-container {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .preview-section {
    position: static;
    order: -1;
  }
  
  .form-grid.three-columns {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .page-header {
    padding: 20px;
  }
  
  .page-title {
    font-size: 24px;
  }
  
  .main-container {
    padding: 0 15px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .form-grid.three-columns {
    grid-template-columns: 1fr;
  }
  
  .image-upload-section {
    flex-direction: column;
    gap: 20px;
  }
  
  .spec-inputs {
    grid-template-columns: 1fr;
  }
  
  .detail-images-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
  
  .detail-image-item {
    width: 100px;
    height: 100px;
  }
  
  .detail-image-upload :deep(.el-upload) {
    width: 100px;
    height: 100px;
  }
}

/* Element UI 样式覆盖 */
.product-form :deep(.el-form-item) {
  margin-bottom: 0;
}

.product-form :deep(.el-form-item__label) {
  display: none;
}

.product-form :deep(.el-input__inner) {
  border-radius: 8px;
  border: 1px solid #e4e7ed;
  transition: all 0.3s ease;
}

.product-form :deep(.el-input__inner:focus) {
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

.product-form :deep(.el-textarea__inner) {
  border-radius: 8px;
  border: 1px solid #e4e7ed;
  transition: all 0.3s ease;
}

.product-form :deep(.el-textarea__inner:focus) {
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

.product-form :deep(.el-select) {
  width: 100%;
}

.product-form :deep(.el-input-number) {
  width: 100%;
}

.product-form :deep(.el-input-number .el-input__inner) {
  text-align: left;
}

/* 富文本编辑器样式 - 现代化UI */
.product-form :deep(.quill-editor) {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.product-form :deep(.ql-toolbar.ql-snow) {
  border: 1px solid #e8e8e8;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  padding: 8px 12px;
  background: #fafafa;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  line-height: 1;
  display: flex;
  align-items: center;
  min-height: 42px;
}

.product-form :deep(.ql-toolbar.ql-snow .ql-formats) {
  margin-right: 12px;
  display: flex;
  align-items: center;
  gap: 2px;
}

.product-form :deep(.ql-toolbar.ql-snow .ql-formats:last-child) {
  margin-right: 0;
}

.product-form :deep(.ql-container.ql-snow) {
  border: 1px solid #e8e8e8;
  border-radius: 0 0 8px 8px;
  font-size: 14px;
  height: 300px;
}

.product-form :deep(.ql-editor) {
  padding: 16px 20px;
  line-height: 1.6;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #2c3e50;
  /* 不设置固定字体大小，让字体大小格式生效 */
}

.product-form :deep(.ql-editor.ql-blank::before) {
  font-style: normal;
  color: #bbb;
  left: 20px;
  right: 20px;
}

/* 工具栏按钮样式 - 统一高度 */
.product-form :deep(.ql-toolbar button) {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #666;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  flex-shrink: 0;
}

.product-form :deep(.ql-toolbar button:hover) {
  background: rgba(64, 158, 255, 0.1);
  color: #409eff;
}

.product-form :deep(.ql-toolbar button.ql-active) {
  background: #409eff;
  color: #fff;
}

.product-form :deep(.ql-toolbar .ql-picker) {
  color: #666;
  transition: all 0.2s ease;
  height: 32px;
  display: inline-flex;
  align-items: center;
}

.product-form :deep(.ql-toolbar .ql-picker:hover) {
  color: #409eff;
}

.product-form :deep(.ql-toolbar .ql-picker.ql-expanded) {
  color: #409eff;
}

.product-form :deep(.ql-toolbar .ql-picker-label) {
  border-radius: 4px;
  padding: 0 8px;
  font-size: 13px;
  line-height: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  min-width: 50px;
}

.product-form :deep(.ql-toolbar .ql-picker-label:hover) {
  background: rgba(64, 158, 255, 0.1);
}

.product-form :deep(.ql-toolbar .ql-picker-options) {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
  z-index: 9999;
  padding: 4px 0;
  min-width: 120px;
}

.product-form :deep(.ql-toolbar .ql-picker-item) {
  padding: 8px 12px;
  border-radius: 0;
  margin: 0;
  transition: all 0.2s ease;
  line-height: 1.2;
}

.product-form :deep(.ql-toolbar .ql-picker-item:hover) {
  background: rgba(64, 158, 255, 0.1);
  color: #409eff;
}

/* 颜色选择器特殊优化 */
.product-form :deep(.ql-toolbar .ql-color .ql-picker-label),
.product-form :deep(.ql-toolbar .ql-background .ql-picker-label) {
  width: 32px;
  min-width: 32px;
  padding: 0;
}

/* 字体大小选择器优化 */
.product-form :deep(.ql-toolbar .ql-size .ql-picker-label) {
  min-width: 60px;
  justify-content: center;
}

/* 字体大小选择器样式 */
.product-form :deep(.ql-size .ql-picker-label) {
  font-family: inherit !important;
}

.product-form :deep(.ql-size .ql-picker-label[data-value="10px"]::before) {
  content: '10px' !important;
}

.product-form :deep(.ql-size .ql-picker-label[data-value="12px"]::before) {
  content: '12px' !important;
}

.product-form :deep(.ql-size .ql-picker-label[data-value="14px"]::before) {
  content: '14px' !important;
}

.product-form :deep(.ql-size .ql-picker-label[data-value="16px"]::before) {
  content: '16px' !important;
}

.product-form :deep(.ql-size .ql-picker-label[data-value="18px"]::before) {
  content: '18px' !important;
}

.product-form :deep(.ql-size .ql-picker-label[data-value="20px"]::before) {
  content: '20px' !important;
}

.product-form :deep(.ql-size .ql-picker-label[data-value="24px"]::before) {
  content: '24px' !important;
}

.product-form :deep(.ql-size .ql-picker-label[data-value="32px"]::before) {
  content: '32px' !important;
}

.product-form :deep(.ql-size .ql-picker-label:not([data-value])::before) {
  content: '16px' !important;
}

.product-form :deep(.ql-size .ql-picker-item[data-value="10px"]::before) {
  content: '10px';
  font-size: 10px;
}

.product-form :deep(.ql-size .ql-picker-item[data-value="12px"]::before) {
  content: '12px';
  font-size: 12px;
}

.product-form :deep(.ql-size .ql-picker-item[data-value="14px"]::before) {
  content: '14px';
  font-size: 14px;
}

.product-form :deep(.ql-size .ql-picker-item[data-value="16px"]::before) {
  content: '16px';
  font-size: 16px;
}

.product-form :deep(.ql-size .ql-picker-item[data-value="18px"]::before) {
  content: '18px';
  font-size: 18px;
}

.product-form :deep(.ql-size .ql-picker-item[data-value="20px"]::before) {
  content: '20px';
  font-size: 20px;
}

.product-form :deep(.ql-size .ql-picker-item[data-value="24px"]::before) {
  content: '24px';
  font-size: 24px;
}

.product-form :deep(.ql-size .ql-picker-item[data-value="32px"]::before) {
  content: '32px';
  font-size: 32px;
}

/* 对齐选择器样式 - 强制显示选项 */
.product-form :deep(.ql-align .ql-picker-label) {
  min-width: 32px;
  justify-content: center;
}

.product-form :deep(.ql-align .ql-picker-options) {
  min-width: 90px !important;
}

.product-form :deep(.ql-align .ql-picker-item) {
  padding: 8px 12px !important;
  position: relative;
  line-height: 1.2;
}

.product-form :deep(.ql-align .ql-picker-item::before) {
  font-family: inherit !important;
  font-size: 13px !important;
}

.product-form :deep(.ql-align .ql-picker-item:first-child::before) {
  content: '左对齐' !important;
}

.product-form :deep(.ql-align .ql-picker-item:nth-child(2)::before) {
  content: '居中' !important;
}

.product-form :deep(.ql-align .ql-picker-item:nth-child(3)::before) {
  content: '右对齐' !important;
}

.product-form :deep(.ql-align .ql-picker-item:nth-child(4)::before) {
  content: '两端对齐' !important;
}

/* 工具提示样式 */
.product-form :deep(.ql-snow .ql-tooltip) {
  z-index: 9999;
  background: #2c3e50;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.product-form :deep(.ql-snow .ql-tooltip input) {
  border: 1px solid #e8e8e8;
  border-radius: 3px;
  padding: 4px 8px;
}

/* 富文本内容样式 - 列表和对齐 */
.product-form :deep(.ql-editor) {
  padding: 16px 20px;
  line-height: 1.6;
}

/* 字体大小应用样式 - Quill实际使用的格式 */
.product-form :deep(.ql-editor span[style*="font-size: 10px"]) {
  font-size: 10px !important;
}

.product-form :deep(.ql-editor span[style*="font-size: 12px"]) {
  font-size: 12px !important;
}

.product-form :deep(.ql-editor span[style*="font-size: 14px"]) {
  font-size: 14px !important;
}

.product-form :deep(.ql-editor span[style*="font-size: 16px"]) {
  font-size: 16px !important;
}

.product-form :deep(.ql-editor span[style*="font-size: 18px"]) {
  font-size: 18px !important;
}

.product-form :deep(.ql-editor span[style*="font-size: 20px"]) {
  font-size: 20px !important;
}

.product-form :deep(.ql-editor span[style*="font-size: 24px"]) {
  font-size: 24px !important;
}

.product-form :deep(.ql-editor span[style*="font-size: 32px"]) {
  font-size: 32px !important;
}

/* Quill字体大小类格式支持 */
.product-form :deep(.ql-editor .ql-size-10px) {
  font-size: 10px !important;
}

.product-form :deep(.ql-editor .ql-size-12px) {
  font-size: 12px !important;
}

.product-form :deep(.ql-editor .ql-size-14px) {
  font-size: 14px !important;
}

.product-form :deep(.ql-editor .ql-size-16px) {
  font-size: 16px !important;
}

.product-form :deep(.ql-editor .ql-size-18px) {
  font-size: 18px !important;
}

.product-form :deep(.ql-editor .ql-size-20px) {
  font-size: 20px !important;
}

.product-form :deep(.ql-editor .ql-size-24px) {
  font-size: 24px !important;
}

.product-form :deep(.ql-editor .ql-size-32px) {
  font-size: 32px !important;
}

/* 确保内联样式生效 - 这是Quill使用的主要方式 */
.product-form :deep(.ql-editor [style]) {
  /* 让内联样式优先级更高 */
}

/* 有序列表样式 */
.product-form :deep(.ql-editor ol) {
  padding-left: 2em;
  margin: 12px 0;
}

.product-form :deep(.ql-editor ol li) {
  list-style-type: decimal;
  margin: 4px 0;
  padding-left: 0.5em;
}

/* 无序列表样式 */
.product-form :deep(.ql-editor ul) {
  padding-left: 2em;
  margin: 12px 0;
}

.product-form :deep(.ql-editor ul li) {
  list-style-type: disc;
  margin: 4px 0;
  padding-left: 0.5em;
}

/* 对齐样式 */
.product-form :deep(.ql-editor .ql-align-center) {
  text-align: center !important;
}

.product-form :deep(.ql-editor .ql-align-right) {
  text-align: right !important;
}

.product-form :deep(.ql-editor .ql-align-justify) {
  text-align: justify !important;
}

.product-form :deep(.ql-editor p.ql-align-center) {
  text-align: center !important;
}

.product-form :deep(.ql-editor p.ql-align-right) {
  text-align: right !important;
}

.product-form :deep(.ql-editor p.ql-align-justify) {
  text-align: justify !important;
}

/* 引用样式 */
.product-form :deep(.ql-editor blockquote) {
  border-left: 4px solid #ccc;
  margin: 16px 0;
  padding: 8px 16px;
  background-color: #f9f9f9;
  font-style: italic;
}

/* 代码块样式 */
.product-form :deep(.ql-editor pre.ql-syntax) {
  background-color: #f4f4f4;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin: 12px 0;
  padding: 12px;
  overflow-x: auto;
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
}

/* 富文本编辑器在产品表单中的特殊样式 */
.rich-description-tips {
  margin-top: 8px;
}

.rich-description-tips :deep(.el-alert) {
  border-radius: 4px;
}

/* 会员折上折设置样式 */
.membership-discount-section {
  background: #fff;
}

.no-levels {
  text-align: center;
  padding: 40px 0;
  color: #909399;
}

.no-levels i {
  font-size: 32px;
  margin-bottom: 12px;
  display: block;
}

.section-description {
  margin-bottom: 20px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #409eff;
}

.description-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #606266;
}

.description-item:last-child {
  margin-bottom: 0;
}

.description-item i {
  color: #409eff;
  font-size: 16px;
}

.levels-selection {
  background: #fff;
}

.selection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e4e7ed;
}

.selection-title {
  font-weight: 500;
  color: #303133;
  font-size: 15px;
}

.levels-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.level-checkbox {
  margin: 0 !important;
  width: 100%;
}

.level-checkbox :deep(.el-checkbox__label) {
  width: 100%;
  padding-left: 8px;
}

.level-card {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
  transition: all 0.3s ease;
  cursor: pointer;
}

.level-checkbox.is-checked .level-card {
  border-color: #409eff;
  background: #f0f9ff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
}

.level-card:hover {
  border-color: #c6e2ff;
  box-shadow: 0 2px 4px rgba(64, 158, 255, 0.1);
}

.level-info {
  margin-bottom: 12px;
}

.level-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
}

.level-discount {
  font-size: 13px;
  color: #e6a23c;
  font-weight: 500;
  margin-bottom: 4px;
}

.level-condition {
  font-size: 12px;
  color: #909399;
}

.level-preview {
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
}

.preview-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 13px;
}

.preview-row:last-child {
  margin-bottom: 0;
}

.preview-row span:first-child {
  color: #909399;
}

.member-price {
  color: #e6a23c;
  font-weight: 500;
}

.discount-price {
  color: #67c23a;
  font-weight: 600;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .levels-grid {
    grid-template-columns: 1fr;
  }
  
  .selection-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}

/* 卡片头部操作按钮 */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.card-header h3 {
  margin: 0;
  flex: 1;
}

.header-actions {
  margin-left: 20px;
}

/* 折上折设置弹窗样式 */
.discount-settings-dialog :deep(.el-dialog__body) {
  padding: 20px;
  max-height: 60vh;
  overflow-y: auto;
}

.discount-settings-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.settings-description {
  margin-bottom: 20px;
}

.alert-content p {
  margin: 0;
  line-height: 1.6;
}

.alert-content p:first-child {
  margin-bottom: 8px;
}

.discount-settings-form {
  max-height: 400px;
  overflow-y: auto;
}

.level-setting-item {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
}

.level-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #fafbfc;
  border-bottom: 1px solid #e4e7ed;
}

.level-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.level-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.level-base-rate {
  font-size: 12px;
  color: #e6a23c;
  background: #fdf6ec;
  padding: 2px 8px;
  border-radius: 12px;
}


.dialog-footer {
  text-align: right;
}

/* 弹窗响应式设计 */
@media (max-width: 768px) {
  .discount-settings-dialog :deep(.el-dialog) {
    width: 95% !important;
    margin: 5vh auto !important;
  }
  
  .level-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style> 